import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { deleteRopa, getRopasShortPaged } from 'services/report-ropa-service.js';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Dialog, PageHeader } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BG = 'var(--vo-mist)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';

const SCOPE_BADGE = {
  'Empresa':           { color: 'var(--vo-cobalto)', bg: 'var(--vo-cobalto-8)' },
  'Área':              { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  'Processo':          { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  'Sumário Executivo': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
};

/* ── Page header ── */
const AddBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Info card ── */
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;
const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ── Table card ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${p => p.$center ? 'center' : 'left'};
  border-bottom: 1px solid ${DIVIDER};
  background: ${BG};
`;
const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const Td = styled.td`
  padding: 13px 16px;
`;
const NameCell = styled(Td)`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
`;
const DateCell = styled(Td)`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const ScopeBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;
const ActionTd = styled(Td)`
  text-align: center;
`;
const RowIconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  &:hover {
    background: ${p => p.$danger ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${p => p.$danger ? 'var(--vo-danger)' : BLUE};
  }
`;

/* ── Pagination ── */
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  border-top: 1px solid var(--vo-mist);
  gap: 4px;
`;
const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border: ${p => p.$active ? 'none' : `1px solid ${BORDER}`};
  background: ${p => p.$active ? BLUE : 'white'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.$active ? '700' : '400'};
  color: ${p => p.$active ? 'white' : 'var(--vo-ink-2)'};
  &:disabled { cursor: default; opacity: 0.4; }
`;

/* ── Empty / loading states ── */
const StateCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 48px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  text-align: center;
  color: ${MUTED};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
`;

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('pt-BR');
};

const getScopeBadge = scope =>
  SCOPE_BADGE[scope] || { color: MUTED, bg: BG };

const ReportsRopa = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const { isLoading, error, data: pagedRopa } = useQuery(
    ['getRopas', reFetch, page],
    async () => {
      if (selectedTenant.slug === undefined) return { data: [], page: 1, totalPages: 0 };
      const result = await getRopasShortPaged(selectedTenant.slug, page, 15);
      if (result?.data.data.length > 0) return result?.data;
      return { data: [], page, totalPages: 0 };
    }
  );

  const onDelete = item => { setItemToDelete(item); setOpen(true); };
  const onClose = () => { setItemToDelete(null); setOpen(false); };
  const onConfirm = async () => {
    try {
      const result = await deleteRopa(itemToDelete?._id);
      if (result.status === 204) { onClose(); setRefetch(!reFetch); }
      else throw new Error(t('dashboard:ropa.error'));
    } catch (err) {
      Dialog.alert({
        title: t('dashboard:alert.errorTitle'),
        message: err.response?.data?.message ?? err.message,
        icon: 'error',
      });
    }
  };

  const rows = pagedRopa?.data ?? [];
  const totalPages = pagedRopa?.totalPages ?? 0;

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="reports-ropa" />

      <PageHeader
        title={t('dashboard:ropa.title')}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('dashboard:ropa.breadcrumbLabel') },
        ]}
        actions={
          <AddBtn onClick={() => navigate('/reports/ropa/add')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('dashboard:ropa.addButton')}
          </AddBtn>
        }
      />

      <InfoCard>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <line x1="9" y1="12" x2="15" y2="12" />
          <line x1="9" y1="16" x2="15" y2="16" />
        </svg>
        <div>
          <InfoTitle>{t('dashboard:ropa.infoTitle')}</InfoTitle>
          <InfoDesc>{t('dashboard:ropa.infoDesc')}</InfoDesc>
        </div>
      </InfoCard>

      {isLoading && (
        <StateCard>{t('common:labels.loading')}</StateCard>
      )}

      {!isLoading && error && (
        <StateCard>{t('dashboard:ropa.error')}</StateCard>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <StateCard>{t('dashboard:ropa.noData')}</StateCard>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <TableCard>
          <StyledTable>
            <thead>
              <tr>
                <Th>{t('common:table.name')}</Th>
                <Th>{t('dashboard:ropa.table.scope')}</Th>
                <Th>{t('dashboard:ropa.table.generatedAt')}</Th>
                <Th $center>{t('common:table.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(ropa => {
                const badge = getScopeBadge(ropa.scope);
                return (
                  <Tr key={ropa._id}>
                    <NameCell>{ropa.name}</NameCell>
                    <Td>
                      <ScopeBadge $color={badge.color} $bg={badge.bg}>
                        {ropa.scope}
                      </ScopeBadge>
                    </Td>
                    <DateCell>{formatDate(ropa.date)}</DateCell>
                    <ActionTd>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <RowIconBtn
                          title={t('common:tooltips.view')}
                          onClick={() => navigate(`/reports/ropa/show/${ropa._id}`)}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                        </RowIconBtn>
                        <RowIconBtn
                          $danger
                          title={t('common:tooltips.delete')}
                          onClick={() => onDelete(ropa)}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </RowIconBtn>
                      </div>
                    </ActionTd>
                  </Tr>
                );
              })}
            </tbody>
          </StyledTable>

          {totalPages > 1 && (
            <PaginationRow>
              <PageBtn
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <PageBtn key={p} $active={p === page} onClick={() => setPage(p)}>
                  {p}
                </PageBtn>
              ))}
              <PageBtn
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </PageBtn>
            </PaginationRow>
          )}
        </TableCard>
      )}

      <Dialog
        open={open}
        onConfirm={onConfirm}
        onCancel={onClose}
        message={t('dashboard:ropa.delete.confirm', { name: itemToDelete?.name })}
      />
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo aos Relatórios ROPA (Registro de Operações de Processamento de Dados)! Aqui você gerencia e visualiza os relatórios de conformidade com a LGPD.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique no título para voltar à página anterior.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Use o botão "Adicionar" para criar um novo relatório ROPA, documentando as operações de processamento de dados da sua organização.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela lista todos os relatórios ROPA criados. Cada relatório documenta atividades de processamento, finalidades, categorias de dados e medidas de segurança.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas quando houver muitos relatórios.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Mantenha seus relatórios ROPA atualizados para garantir conformidade contínua com a LGPD.',
    disableScrolling: true,
    placement: 'center',
  },
];

export default ReportsRopa;
